import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { LandingRateBand } from "@/shared/api";
import {
  emptyDraft,
  endOfMonth,
  nextDay,
  semiMonthlyDrafts,
  validate,
  type RateBandDraft,
} from "./rateBands";

export type { RateBandDraft } from "./rateBands";

export interface RateBandsState {
  drafts: RateBandDraft[];
  /** Per-row message, keyed by draft id. Empty when the row is valid. */
  errors: Record<string, string>;
  /** Set when the schedule as a whole is wrong - overlapping spans. */
  scheduleError: string | null;
  /** Parsed bands, or null while anything is invalid. */
  bands: LandingRateBand[] | null;
  add: () => void;
  update: (id: string, patch: Partial<Omit<RateBandDraft, "id">>) => void;
  remove: (id: string) => void;
  reset: () => void;
  /**
   * Replaces the rows with a split at the 15th, the shape freight has actually
   * been re-quoted in. A no-op when no month is known.
   */
  seedSemiMonthly: () => void;
  /** False when no month is known, so the seed button can hide. */
  canSeed: boolean;
}

/**
 * State for the optional dated landing rates.
 *
 * Empty by default, and empty is meaningful: a run with no bands applies the one
 * flat rate to every line, which is how every close before August 2026 worked
 * and what keeps those closes reproducible.
 *
 * Every rule lives in {@link ./rateBands}; this is state and callbacks only.
 *
 * @param seedMonth Any ISO date inside the month the seed button should split.
 *
 * @example
 * const bands = useRateBands("2026-08-01");
 * bands.seedSemiMonthly();
 * bands.update(bands.drafts[0].id, { rate: "2.29" });
 */
export function useRateBands(seedMonth?: string): RateBandsState {
  const [drafts, setDrafts] = useState<RateBandDraft[]>([]);
  const { errors, scheduleError, bands } = useMemo(() => validate(drafts), [drafts]);

  return { drafts, errors, scheduleError, bands, ...useRateBandActions(setDrafts, seedMonth) };
}

type SetDrafts = Dispatch<SetStateAction<RateBandDraft[]>>;

type RateBandActions = Pick<
  RateBandsState,
  "add" | "update" | "remove" | "reset" | "seedSemiMonthly" | "canSeed"
>;

/** The mutations, split out so each stays small enough to read at a glance. */
function useRateBandActions(setDrafts: SetDrafts, seedMonth?: string): RateBandActions {
  const add = useCallback(() => {
    setDrafts((current) => {
      // A new row starts the day after the last one ends, because bands are
      // written in sequence and retyping a date the form already knows is the
      // most likely place to fat-finger a gap or an overlap.
      const previous = current.at(-1);
      const startsOn = previous?.endsOn ? nextDay(previous.endsOn) : (seedMonth ?? "");
      return [...current, emptyDraft({ startsOn, endsOn: startsOn ? endOfMonth(startsOn) : "" })];
    });
  }, [setDrafts, seedMonth]);

  const seedSemiMonthly = useCallback(() => {
    if (seedMonth) setDrafts(semiMonthlyDrafts(seedMonth));
  }, [setDrafts, seedMonth]);

  const update = useCallback(
    (id: string, patch: Partial<Omit<RateBandDraft, "id">>) => {
      setDrafts((current) =>
        current.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)),
      );
    },
    [setDrafts],
  );

  const remove = useCallback(
    (id: string) => setDrafts((current) => current.filter((draft) => draft.id !== id)),
    [setDrafts],
  );

  const reset = useCallback(() => setDrafts([]), [setDrafts]);

  return { add, update, remove, reset, seedSemiMonthly, canSeed: Boolean(seedMonth) };
}
